import i18n from 'i18next';
import moment, { Moment } from 'moment';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import InsuranceCoverage from '@r6digital/rapidstor-types/types/InsuranceCoverage';
import { BaseReservation } from '@r6digital/rapidstor-types/types/Reservation';

import { CorporationCode, CorpTranslation, getCorpTranslations } from '@/features/translations/helpers';

import { formatCurrency } from './currency';

export const payNowButtonText = (location: Location): string => {
    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode);

    if (!corpTranslations) {
        return location.rapidstor.payNowButtonText;
    }

    if (!('rapidstor' in corpTranslations)) {
        return location.rapidstor.payNowButtonText;
    }

    if ('payNowButtonText' in corpTranslations.rapidstor) {
        return corpTranslations.rapidstor.payNowButtonText;
    }

    return location.rapidstor.payNowButtonText;
};

export const payLaterButtonText = (location: Location): string => {
    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode);

    if (!corpTranslations) {
        return location.rapidstor.payLaterButtonText;
    }

    if (!('rapidstor' in corpTranslations)) {
        return location.rapidstor.payLaterButtonText;
    }

    if ('payLaterButtonText' in corpTranslations.rapidstor) {
        return corpTranslations.rapidstor.payLaterButtonText;
    }

    return location.rapidstor.payLaterButtonText;
};

export const saveQuoteButtonText = (location: Location): string => {
    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode);

    if (!corpTranslations) {
        return location.rapidstor.saveQuoteButtonText;
    }

    if (!('rapidstor' in corpTranslations)) {
        return location.rapidstor.saveQuoteButtonText;
    }

    if ('saveQuoteButtonText' in corpTranslations.rapidstor) {
        return corpTranslations.rapidstor.saveQuoteButtonText;
    }

    return location.rapidstor.saveQuoteButtonText;
};

export const showPricesButtonText = (location: Location, preferRapidStorText = false): string => {
    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode) as CorpTranslation;

    if (!corpTranslations || preferRapidStorText) {
        return location.rapidstor.showPricesButtonText;
    }

    if (!corpTranslations.rapidstor?.showPricesButtonText) {
        return location.rapidstor.showPricesButtonText;
    } else if (corpTranslations.rapidstor?.showPricesButtonText) {
        return corpTranslations.rapidstor.showPricesButtonText as string;
    }

    if (corpTranslations.contact_card?.showPrices?.title) {
        return corpTranslations.contact_card.showPrices.title as string;
    }

    return location.rapidstor.showPricesButtonText;
};

export const showPricesTitleText = (location: Location): string => {
    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode) as CorpTranslation;

    if (!corpTranslations) {
        return location.rapidstor.showPricesButtonText;
    }

    if (corpTranslations.contact_card?.showPrices?.title) {
        return corpTranslations.contact_card.showPrices.title as string;
    }

    return location.rapidstor.showPricesButtonText;
};

export const noAvailableUnitText = (location: Location): string => {
    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode);

    if (!corpTranslations) {
        return location.rapidstor.noAvailableUnitsText;
    }

    if (!('rapidstor' in corpTranslations)) {
        return location.rapidstor.noAvailableUnitsText;
    }

    if ('noAvailableUnitsText' in corpTranslations.rapidstor) {
        return corpTranslations.rapidstor.noAvailableUnitsText;
    }

    return location.rapidstor.noAvailableUnitsText ?? i18n.t('cta:call_us_now');    
};

export const descriptorDimensions = (
    unitType: UnitType,
    location: Location,
    exclSizeSymbols?: boolean
): string => {
    return `${exclSizeSymbols ? `` : `(`}${unitType.width}${
        location.rapidstor.sizeSymbol
    }${exclSizeSymbols ? ` × ` : ` W/`}${unitType.length}${
        location.rapidstor.sizeSymbol
    }${exclSizeSymbols ? '' : ` L)`}`;
};

export const unitTypeHasDeal = (unitType: UnitType): boolean => {
    return unitType.deal !== undefined;
};

export const unitTypeHasDiscount = (unitType: UnitType): boolean => {
    return unitType.rates.discounted;
};

export const unitTypeDiscountedRate = (unitType: UnitType): number => {
    return unitType.deal?.rates.discountedRate ?? unitType.rates.discountedRate;
};

export const unitTypeRate = (unitType: UnitType): number => {
    return unitType.rates.rate;
};

export const unitTypeDisplayRate = (unitType: UnitType): number => {
    return unitType.deal ? unitTypeDiscountedRate(unitType) : unitTypeRate(unitType);
};

export const unitTypeValueRate = (unitType: UnitType, location: Location): string => {
    return formatCurrency(unitTypeRate(unitType), location);
};

export const unitTypeFormattedRate = (unitType: UnitType, location: Location): string => {
    return formatCurrency(unitTypeDisplayRate(unitType), location);
};

export const unitTypeDealSavingsPercentage = (unitType: UnitType): string => {
    const val =
        ((unitType.deal?.rates.discountSaved ?? 0) / (unitType.deal?.rates.rate ?? 0)) *
        100;
    return `${val.toFixed(0)}%`;
};

export const unitTypeDealInclusions = (unitType: UnitType): string[] => {
    return unitType.deal ? unitType.deal.descriptions : [];
};

/**
 * Get the date range for the date picker 
 *
 * @param location RapidStor location
 * @param type 'movein' or 'reservation'
 * @returns Date Range object used in react-datepicker min and max values
 */
export const getDatePickerRange = (location: Location, type: 'movein' | 'reservation'): Record<string, Date | undefined> => {
    let maxDate = null;

    const minDates = [moment()];
    if (location.rapidstor.siteOpeningDate) {  
        minDates.push(moment(location.rapidstor.siteOpeningDate, "DD-MM-YYYY"));  
    }  
    if (location.rapidstor.reservationNumberOfDaysInAdvanceMinimum) {  
        minDates.push(moment().add(location.rapidstor.reservationNumberOfDaysInAdvanceMinimum, 'days'));  
    }  
    const minDate = moment.max(minDates);  

    const limit = {  
        'movein': location.rapidstor.moveinNumberOfDaysInAdvanceLimit,  
        'reservation': location.rapidstor.reservationNumberOfDaysInAdvanceLimit,  
    }[type]; 

    if (limit) {
        maxDate = moment().add(limit, 'days');
    }

    return {
        min: minDate.toDate(),
        max: maxDate ? maxDate.toDate() : undefined,
    };
}

/**
 * Get the default date for the date picker 
 *
 * @param location RapidStor location
 * @param now Moment object representing the current date
 *
 * @returns Default date for the date picker
 */
export const getDefaultNeededDate = (location: Location, now: Moment): Date => {
    const excludedDates = location.rapidstor.datesClosed
        .split(',')
        .map((d) => moment(d, 'DD-MM-YYYY'));

    let defaultDate = now.clone();

    if (location.rapidstor.reservationNumberOfDaysInAdvanceMinimum) {
        defaultDate = defaultDate.add(location.rapidstor.reservationNumberOfDaysInAdvanceMinimum, 'days');
    }

    if (excludedDates.find((d) => d.isSame(defaultDate, 'day'))) {
        let nextDate = defaultDate.clone().add(1, 'days');
        while (excludedDates.find((d) => d.isSame(nextDate, 'day'))) {
            nextDate = nextDate.add(1, 'days');
        }
        return nextDate.toDate();
    }

    return defaultDate.toDate();
}

export const RapidStorSettings = (location: Location) => {
    return {

        customerType: () => {
            return {
                show: () => {
                    return location.rapidstor.wizards.moveinSingle.distinguishCustomerTypes;
                }
            }
        },

        dateOfBirth: () => {
            return {
                show: () => {
                    return !location.rapidstor.wizards.moveinSingle.hideDateOfBirth;
                }
            }
        },

        state: () => {
            return {
                show: () => {
                    return !location.rapidstor.wizards.moveinSingle.hideState;
                }
            }
        },

        postalCode: () => {
            return {
                show: () => {
                    return !location.rapidstor.wizards.moveinSingle.hidePostalCode;
                }
            }
        },

        alternateContact: () => {
            return {
                show: () => {
                    return !location.rapidstor.wizards.moveinSingle.hideAlternativeContact;
                },
                phoneNumber: () => {
                    return {
                        required: () => {
                            return location.rapidstor.wizards.moveinSingle.requireAlternativeContactPhoneNumberField;
                        }
                    }
                },
                addressDetails: () => {
                    return {
                        required: () => {
                            return location.rapidstor.wizards.moveinSingle.requireAlternativeContactAddressDetails;
                        }
                    }
                },
                email: () => {
                    return {
                        required: () => {
                            return location.rapidstor.wizards.moveinSingle.requireAlternativeContactEmailField;
                        }
                    }
                }
            }
        },

        passwordFields: () => {
            return {
                show: () => {
                    return !location.rapidstor.wizards.moveinSingle.hidePasswordFields;
                },
            }
        },

        identityVerification: () => {
            return {
                show: () => {
                    return location.rapidstor.wizards.moveinSingle.allowIdUpload;
                }
            }
        },

        insurance: () => {
            return {
                show: (reservation?: BaseReservation, coverages?: InsuranceCoverage[]) => {
                    if (location.rapidstor.wizards.moveinSingle.hideInsurance) {
                        return false;
                    }
        
                    if (!reservation || reservation.unitType.excludeFromInsurance) {
                        return false;
                    }
        
                    if (!coverages || coverages.length === 0) {
                        return false;
                    }
        
                    return true;
                },
                required: () => {
                    return location.rapidstor.wizards.moveinSingle.insuranceRequired;
                },
                optional: () => {
                    return !location.rapidstor.wizards.moveinSingle.insuranceRequired;
                },
                manualEntry: () => {
                    return {
                        required: () => {
                            return location.rapidstor.wizards.moveinSingle.insuranceManualEntryRequired;
                        },
                        show: () => {
                            return location.rapidstor.wizards.moveinSingle.insuranceManualEntry;
                        }
                    }
                }
            }
        },

        proofOfInsurance: () => {
            return {
                show: (insuranceType: string) => {
                    if (location.rapidstor.wizards.moveinSingle.hideInsurance) {
                        return false;
                    }
        
                    if (insuranceType === 'self' || location.rapidstor.wizards.moveinSingle.allowProofOfInsuranceUpload) {
                        return true;
                    }
        
                    return false;
                }
            }
        },

        proofOfAddress: () => {
            return {
                show: () => {
                    return location.rapidstor.wizards.moveinSingle.allowProofOfAddressUpload;
                }
            }
        },

        boxshop: () => {
            return {
                show: () => {
                    return !location.rapidstor.wizards.moveinSingle.hideBoxshop;
                },

                coupons: () => {
                    return {
                        show: () => {
                            return location.rapidstor.useBoxShopCoupons;
                        }
                    }
                }
            }
        },

        paymentForm: () => {
            return {
                show: () => {
                    return !location.rapidstor.paymentGateways.use;
                }
            }
        },

        autobill: () => {
            return {
                show: () => {
                    return location.rapidstor.useAutoBilling && !location.rapidstor.paymentGateways.use;
                },
                required: () => {
                    return location.rapidstor.requireAutobill;
                }
            }
        },

        driversLicense: () => {
            return {
                show: () => {
                    return !location.rapidstor.wizards.moveinSingle.hideDriversLicence;
                }
            }
        },
        
        showSaveQuoteButton: () => {
            return location.rapidstor.allowSaveQuote;
        },

        showReservationButton: () => {
            return location.rapidstor.allowReservation;
        },

        showMoveInButton: () => {
            return location.rapidstor.allowMoveIn;
        },

        showDealCoupons: () => {
            return location.rapidstor.useDealCoupons;
        },

    }
}